import { api } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { AcceptedInvitation, InvitationPreview, RoleOption } from '@/features/auth/types'
import { demoInvitations } from '@/features/invitations/demo/demoInvitations'
import type { InviteInput } from '@/features/invitations/schemas/invitationSchemas'
import type { InvitationCreated } from '@/features/invitations/types'
import { env } from '@/lib/env'

/**
 * Invitations replace self-registration. A branch manager or company admin
 * invites someone into a named location; the invitee opens the link, sets a
 * password, then signs in like everyone else. The backend sends no e-mail — the
 * link travels by hand.
 */
/** The request names its Standort; the backend never takes it from the session. */
export function toInviteRequest(input: InviteInput, locationPublicId: string) {
  return { ...input, location: locationPublicId }
}

export const invitationsApi = {
  /** Only the roles a manager may hand out; needs `USER_INVITE`. */
  fetchRoles: () =>
    env.demoMode
      ? demoInvitations.fetchRoles()
      : api.get<RoleOption[]>(endpoints.invitations.roles),

  create: (input: InviteInput, locationPublicId: string) =>
    env.demoMode
      ? demoInvitations.create(input)
      : api.post<InvitationCreated>(
          endpoints.invitations.create,
          toInviteRequest(input, locationPublicId),
        ),

  /** Public. Any unusable token — expired, used, replaced, unknown — is a 404. */
  preview: (token: string) =>
    env.demoMode
      ? demoInvitations.preview(token)
      : api.get<InvitationPreview>(endpoints.invitations.preview(token)),

  /** Public and CSRF-exempt. Creates the account but starts no session. */
  accept: (token: string, password: string) =>
    env.demoMode
      ? demoInvitations.accept(token)
      : api.post<AcceptedInvitation>(endpoints.invitations.accept(token), { password }),
}
